import {
  record,
  generateSelector,
  generateSelectorFromElement,
  generateSelectorFromHTML,
} from 'rrweb';
import type { eventWithTime } from '@rrweb/types';
import { onLCP, onFCP, onINP, onCLS, onTTFB } from 'web-vitals';
import type { Metric, LCPMetric, FCPMetric, INPMetric, CLSMetric, TTFBMetric } from 'web-vitals';

// Re-export selector functions for browser agents to use
export { generateSelector, generateSelectorFromElement, generateSelectorFromHTML };

// ============================================================================
// Types and Interfaces
// ============================================================================

interface NetworkOptions {
  skipUrls?: string[];
  captureHeaders?: boolean;
  captureBody?: boolean;
  maxBodySize?: number;
  sampleRate?: number;
}

interface StorageOptions {
  localStorage?: boolean;
  sessionStorage?: boolean;
  cookies?: boolean;
  sensitiveKeys?: string[];
}

interface ConsoleOptions {
  levels?: ('log' | 'warn' | 'error' | 'info' | 'debug')[];
  maxLogs?: number;
  maxPayloadSize?: number;
  captureStackTrace?: boolean;
}

interface WebVitalsOptions {
  /** Which metrics to capture. Default: ['LCP', 'FCP', 'INP', 'CLS'] */
  allowedMetrics?: ('LCP' | 'FCP' | 'INP' | 'CLS' | 'TTFB')[];
  /** Whether to capture full metric attribution data. Default: true */
  captureAttribution?: boolean;
}

interface SDKConfig {
  apiKey: string;
  endpoint: string;
  debug: boolean;
  batchSize: number;
  batchInterval: number;
  enableRecording: boolean;
  maxRetries: number;
  baseRetryDelay: number;
  enableCompression: boolean;
  // PostHog-like features
  persistence: 'localStorage' | 'cookie' | 'localStorage+cookie' | 'memory' | 'none';
  respect_dnt: boolean;
  autocapture: boolean;
  capture_pageview: boolean;
  capture_pageleave: boolean;
  // Monitoring features
  capture_network: boolean;
  networkOptions: NetworkOptions;
  capture_storage: boolean;
  storageOptions: StorageOptions;
  capture_console: boolean;
  consoleOptions: ConsoleOptions;
  // Web Vitals
  capture_web_vitals: boolean;
  webVitalsOptions: WebVitalsOptions;
  // Bootstrap
  bootstrap?: {
    distinctID?: string;
    isIdentifiedID?: boolean;
  };
}

interface SDKOptions {
  endpoint?: string;
  debug?: boolean;
  batchSize?: number;
  batchInterval?: number;
  enableRecording?: boolean;
  maxRetries?: number;
  baseRetryDelay?: number;
  enableCompression?: boolean;
  // PostHog-like features
  persistence?: 'localStorage' | 'cookie' | 'localStorage+cookie' | 'memory' | 'none';
  respect_dnt?: boolean;
  autocapture?: boolean;
  capture_pageview?: boolean;
  capture_pageleave?: boolean;
  // Monitoring features
  capture_network?: boolean;
  networkOptions?: NetworkOptions;
  capture_storage?: boolean;
  storageOptions?: StorageOptions;
  capture_console?: boolean;
  consoleOptions?: ConsoleOptions;
  // Web Vitals
  capture_web_vitals?: boolean;
  webVitalsOptions?: WebVitalsOptions;
  // Bootstrap
  bootstrap?: {
    distinctID?: string;
    isIdentifiedID?: boolean;
  };
}

interface UserProperties {
  [key: string]: string | number | boolean | null | undefined;
}

interface UserIdentity {
  distinctId: string | null;
  properties: UserProperties;
  isIdentified: boolean;
}

interface BentoEvent {
  timestamp: number;
  type: string;
  data: unknown;
  sessionId: string;
}

interface RetryableBentoEvent extends BentoEvent {
  retryCount: number;
  nextRetryTime: number;
}

interface BatchPayload {
  sessionId: string;
  events: BentoEvent[];
  timestamp: number;
  userAgent: string;
  url: string;
  distinctId?: string;
}

interface CapturedEvent {
  event: string;
  properties: Record<string, unknown>;
  $timestamp: number;
  $session_id: string;
  $distinct_id: string;
}

interface NetworkLog {
  url: string;
  method: string;
  status: number;
  duration: number;
  responseSize: number;
  initiator: 'fetch' | 'xhr';
  error?: string;
  timestamp: number;
}

interface StorageLog {
  storageType: 'localStorage' | 'sessionStorage' | 'cookie';
  operation: 'get' | 'set' | 'remove' | 'clear';
  key: string;
  valueType?: string;
  timestamp: number;
}

interface ConsoleLog {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  payload: string[];
  trace: string[];
  timestamp: number;
}

// Persistence keys
const PERSISTENCE_KEYS = {
  DISTINCT_ID: 'bentolabs_distinct_id',
  SUPER_PROPERTIES: 'bentolabs_super_properties',
  OPTED_OUT: 'bentolabs_opted_out',
  USER_PROPERTIES: 'bentolabs_user_properties',
  IS_IDENTIFIED: 'bentolabs_is_identified',
};

// ============================================================================
// Persistence Class
// ============================================================================

class Persistence {
  private mode: 'localStorage' | 'cookie' | 'localStorage+cookie' | 'memory' | 'none';
  private memoryStorage: Map<string, string>;

  constructor(mode: SDKConfig['persistence']) {
    this.mode = mode;
    this.memoryStorage = new Map();
  }

  get(key: string): string | null {
    if (this.mode === 'none') return null;

    if (this.mode === 'memory') {
      return this.memoryStorage.get(key) || null;
    }

    // Try localStorage first
    if (this.mode === 'localStorage' || this.mode === 'localStorage+cookie') {
      try {
        const value = localStorage.getItem(key);
        if (value !== null) return value;
      } catch {
        // localStorage not available
      }
    }

    // Try cookie
    if (this.mode === 'cookie' || this.mode === 'localStorage+cookie') {
      try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const [cookieKey, cookieValue] = cookie.trim().split('=');
          if (cookieKey === key) {
            return decodeURIComponent(cookieValue);
          }
        }
      } catch {
        // cookies not available
      }
    }

    return null;
  }

  set(key: string, value: string): void {
    if (this.mode === 'none') return;

    if (this.mode === 'memory') {
      this.memoryStorage.set(key, value);
      return;
    }

    // Set in localStorage
    if (this.mode === 'localStorage' || this.mode === 'localStorage+cookie') {
      try {
        localStorage.setItem(key, value);
      } catch {
        // localStorage not available
      }
    }

    // Set in cookie (1 year expiry)
    if (this.mode === 'cookie' || this.mode === 'localStorage+cookie') {
      try {
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        document.cookie = `${key}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      } catch {
        // cookies not available
      }
    }
  }

  remove(key: string): void {
    if (this.mode === 'none') return;

    if (this.mode === 'memory') {
      this.memoryStorage.delete(key);
      return;
    }

    // Remove from localStorage
    if (this.mode === 'localStorage' || this.mode === 'localStorage+cookie') {
      try {
        localStorage.removeItem(key);
      } catch {
        // localStorage not available
      }
    }

    // Remove cookie
    if (this.mode === 'cookie' || this.mode === 'localStorage+cookie') {
      try {
        document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      } catch {
        // cookies not available
      }
    }
  }

  clear(): void {
    if (this.mode === 'memory') {
      this.memoryStorage.clear();
      return;
    }

    // Clear all BentoLabs keys
    Object.values(PERSISTENCE_KEYS).forEach((key) => this.remove(key));
  }
}

// ============================================================================
// Network Monitor
// ============================================================================

class NetworkMonitor {
  private logs: NetworkLog[] = [];
  private options: Required<NetworkOptions>;
  private originalFetch: typeof fetch | null = null;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXHRSend: typeof XMLHttpRequest.prototype.send | null = null;
  private isMonitoring = false;
  private onCapture: (log: NetworkLog) => void;

  constructor(options: NetworkOptions, onCapture: (log: NetworkLog) => void) {
    this.options = {
      skipUrls: options.skipUrls || ['api.bentolabs.ai'],
      captureHeaders: options.captureHeaders ?? false,
      captureBody: options.captureBody ?? false,
      maxBodySize: options.maxBodySize ?? 5000,
      sampleRate: options.sampleRate ?? 1,
    };
    this.onCapture = onCapture;
  }

  start(): void {
    if (this.isMonitoring || typeof window === 'undefined') return;
    this.isMonitoring = true;
    this.interceptFetch();
    this.interceptXHR();
  }

  stop(): void {
    if (!this.isMonitoring) return;
    this.isMonitoring = false;
    this.restoreFetch();
    this.restoreXHR();
  }

  getLogs(): NetworkLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  private shouldSkip(url: string): boolean {
    return this.options.skipUrls.some((skipUrl) => url.includes(skipUrl));
  }

  private shouldSample(): boolean {
    return Math.random() < this.options.sampleRate;
  }

  private sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      // Remove sensitive query params
      const sensitiveParams = ['token', 'key', 'secret', 'password', 'auth', 'apikey', 'api_key'];
      sensitiveParams.forEach((param) => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });
      return urlObj.toString();
    } catch {
      return url;
    }
  }

  private interceptFetch(): void {
    if (typeof fetch === 'undefined') return;

    this.originalFetch = fetch.bind(window);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    (window as unknown as { fetch: typeof fetch }).fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

      if (self.shouldSkip(url) || !self.shouldSample()) {
        return self.originalFetch!(input, init);
      }

      const startTime = Date.now();
      const method = init?.method || 'GET';

      try {
        const response = await self.originalFetch!(input, init);
        const duration = Date.now() - startTime;

        const log: NetworkLog = {
          url: self.sanitizeUrl(url),
          method: method.toUpperCase(),
          status: response.status,
          duration,
          responseSize: parseInt(response.headers.get('content-length') || '0', 10),
          initiator: 'fetch',
          timestamp: Date.now(),
        };

        self.logs.push(log);
        if (self.logs.length > 500) self.logs.shift();
        self.onCapture(log);

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        const log: NetworkLog = {
          url: self.sanitizeUrl(url),
          method: method.toUpperCase(),
          status: 0,
          duration,
          responseSize: 0,
          initiator: 'fetch',
          error: error instanceof Error ? error.message : 'Unknown error',
          timestamp: Date.now(),
        };

        self.logs.push(log);
        if (self.logs.length > 500) self.logs.shift();
        self.onCapture(log);

        throw error;
      }
    };
  }

  private restoreFetch(): void {
    if (this.originalFetch && typeof window !== 'undefined') {
      (window as unknown as { fetch: typeof fetch }).fetch = this.originalFetch;
      this.originalFetch = null;
    }
  }

  private interceptXHR(): void {
    if (typeof XMLHttpRequest === 'undefined') return;

    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async?: boolean,
      username?: string | null,
      password?: string | null
    ) {
      (this as unknown as { _bentoUrl: string })._bentoUrl = url.toString();
      (this as unknown as { _bentoMethod: string })._bentoMethod = method;
      (this as unknown as { _bentoStartTime: number })._bentoStartTime = 0;
      return self.originalXHROpen!.call(this, method, url, async ?? true, username, password);
    };

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      const xhr = this as XMLHttpRequest & {
        _bentoUrl: string;
        _bentoMethod: string;
        _bentoStartTime: number;
      };
      const url = xhr._bentoUrl;

      if (self.shouldSkip(url) || !self.shouldSample()) {
        return self.originalXHRSend!.call(this, body);
      }

      xhr._bentoStartTime = Date.now();

      const handleLoadEnd = () => {
        const duration = Date.now() - xhr._bentoStartTime;
        const log: NetworkLog = {
          url: self.sanitizeUrl(url),
          method: xhr._bentoMethod.toUpperCase(),
          status: xhr.status,
          duration,
          responseSize: parseInt(xhr.getResponseHeader('content-length') || '0', 10),
          initiator: 'xhr',
          timestamp: Date.now(),
        };

        if (xhr.status === 0) {
          log.error = 'Request failed or was aborted';
        }

        self.logs.push(log);
        if (self.logs.length > 500) self.logs.shift();
        self.onCapture(log);
      };

      xhr.addEventListener('loadend', handleLoadEnd);
      return self.originalXHRSend!.call(this, body);
    };
  }

  private restoreXHR(): void {
    if (this.originalXHROpen) {
      XMLHttpRequest.prototype.open = this.originalXHROpen;
      this.originalXHROpen = null;
    }
    if (this.originalXHRSend) {
      XMLHttpRequest.prototype.send = this.originalXHRSend;
      this.originalXHRSend = null;
    }
  }
}

// ============================================================================
// Storage Monitor
// ============================================================================

class StorageMonitor {
  private logs: StorageLog[] = [];
  private options: Required<StorageOptions>;
  private isMonitoring = false;
  private onCapture: (log: StorageLog) => void;
  private originalLocalStorage: {
    setItem: typeof Storage.prototype.setItem;
    getItem: typeof Storage.prototype.getItem;
    removeItem: typeof Storage.prototype.removeItem;
    clear: typeof Storage.prototype.clear;
  } | null = null;
  private originalSessionStorage: {
    setItem: typeof Storage.prototype.setItem;
    getItem: typeof Storage.prototype.getItem;
    removeItem: typeof Storage.prototype.removeItem;
    clear: typeof Storage.prototype.clear;
  } | null = null;
  private originalCookieDescriptor: PropertyDescriptor | null = null;

  constructor(options: StorageOptions, onCapture: (log: StorageLog) => void) {
    this.options = {
      localStorage: options.localStorage ?? true,
      sessionStorage: options.sessionStorage ?? true,
      cookies: options.cookies ?? true,
      sensitiveKeys: options.sensitiveKeys || ['password', 'token', 'secret', 'api_key', 'apikey', 'auth', 'credential'],
    };
    this.onCapture = onCapture;
  }

  start(): void {
    if (this.isMonitoring || typeof window === 'undefined') return;
    this.isMonitoring = true;

    if (this.options.localStorage) this.interceptLocalStorage();
    if (this.options.sessionStorage) this.interceptSessionStorage();
    if (this.options.cookies) this.interceptCookies();
  }

  stop(): void {
    if (!this.isMonitoring) return;
    this.isMonitoring = false;

    this.restoreLocalStorage();
    this.restoreSessionStorage();
    this.restoreCookies();
  }

  getLogs(): StorageLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  private isSensitiveKey(key: string): boolean {
    const lowerKey = key.toLowerCase();
    return this.options.sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive.toLowerCase()));
  }

  private addLog(log: StorageLog): void {
    // Don't log sensitive keys or BentoLabs internal keys
    if (this.isSensitiveKey(log.key) || log.key.startsWith('bentolabs_')) return;

    this.logs.push(log);
    if (this.logs.length > 500) this.logs.shift();
    this.onCapture(log);
  }

  private interceptLocalStorage(): void {
    if (typeof localStorage === 'undefined') return;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.originalLocalStorage = {
      setItem: localStorage.setItem.bind(localStorage),
      getItem: localStorage.getItem.bind(localStorage),
      removeItem: localStorage.removeItem.bind(localStorage),
      clear: localStorage.clear.bind(localStorage),
    };

    localStorage.setItem = function (key: string, value: string) {
      self.addLog({
        storageType: 'localStorage',
        operation: 'set',
        key,
        valueType: typeof value,
        timestamp: Date.now(),
      });
      return self.originalLocalStorage!.setItem(key, value);
    };

    localStorage.getItem = function (key: string) {
      self.addLog({
        storageType: 'localStorage',
        operation: 'get',
        key,
        timestamp: Date.now(),
      });
      return self.originalLocalStorage!.getItem(key);
    };

    localStorage.removeItem = function (key: string) {
      self.addLog({
        storageType: 'localStorage',
        operation: 'remove',
        key,
        timestamp: Date.now(),
      });
      return self.originalLocalStorage!.removeItem(key);
    };

    localStorage.clear = function () {
      self.addLog({
        storageType: 'localStorage',
        operation: 'clear',
        key: '*',
        timestamp: Date.now(),
      });
      return self.originalLocalStorage!.clear();
    };
  }

  private restoreLocalStorage(): void {
    if (this.originalLocalStorage && typeof localStorage !== 'undefined') {
      localStorage.setItem = this.originalLocalStorage.setItem;
      localStorage.getItem = this.originalLocalStorage.getItem;
      localStorage.removeItem = this.originalLocalStorage.removeItem;
      localStorage.clear = this.originalLocalStorage.clear;
      this.originalLocalStorage = null;
    }
  }

  private interceptSessionStorage(): void {
    if (typeof sessionStorage === 'undefined') return;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.originalSessionStorage = {
      setItem: sessionStorage.setItem.bind(sessionStorage),
      getItem: sessionStorage.getItem.bind(sessionStorage),
      removeItem: sessionStorage.removeItem.bind(sessionStorage),
      clear: sessionStorage.clear.bind(sessionStorage),
    };

    sessionStorage.setItem = function (key: string, value: string) {
      self.addLog({
        storageType: 'sessionStorage',
        operation: 'set',
        key,
        valueType: typeof value,
        timestamp: Date.now(),
      });
      return self.originalSessionStorage!.setItem(key, value);
    };

    sessionStorage.getItem = function (key: string) {
      self.addLog({
        storageType: 'sessionStorage',
        operation: 'get',
        key,
        timestamp: Date.now(),
      });
      return self.originalSessionStorage!.getItem(key);
    };

    sessionStorage.removeItem = function (key: string) {
      self.addLog({
        storageType: 'sessionStorage',
        operation: 'remove',
        key,
        timestamp: Date.now(),
      });
      return self.originalSessionStorage!.removeItem(key);
    };

    sessionStorage.clear = function () {
      self.addLog({
        storageType: 'sessionStorage',
        operation: 'clear',
        key: '*',
        timestamp: Date.now(),
      });
      return self.originalSessionStorage!.clear();
    };
  }

  private restoreSessionStorage(): void {
    if (this.originalSessionStorage && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem = this.originalSessionStorage.setItem;
      sessionStorage.getItem = this.originalSessionStorage.getItem;
      sessionStorage.removeItem = this.originalSessionStorage.removeItem;
      sessionStorage.clear = this.originalSessionStorage.clear;
      this.originalSessionStorage = null;
    }
  }

  private interceptCookies(): void {
    if (typeof document === 'undefined') return;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const descriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');
    if (!descriptor) return;
    this.originalCookieDescriptor = descriptor;

    Object.defineProperty(document, 'cookie', {
      get: function () {
        return self.originalCookieDescriptor!.get!.call(this);
      },
      set: function (value: string) {
        // Extract cookie name
        const name = value.split('=')[0].trim();
        self.addLog({
          storageType: 'cookie',
          operation: 'set',
          key: name,
          timestamp: Date.now(),
        });
        return self.originalCookieDescriptor!.set!.call(this, value);
      },
      configurable: true,
    });
  }

  private restoreCookies(): void {
    if (this.originalCookieDescriptor && typeof document !== 'undefined') {
      Object.defineProperty(document, 'cookie', this.originalCookieDescriptor);
      this.originalCookieDescriptor = null;
    }
  }
}

// ============================================================================
// Console Monitor
// ============================================================================

class ConsoleMonitor {
  private logs: ConsoleLog[] = [];
  private options: Required<ConsoleOptions>;
  private isMonitoring = false;
  private inStack = false; // Prevent infinite recursion
  private onCapture: (log: ConsoleLog) => void;
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    error: typeof console.error;
    info: typeof console.info;
    debug: typeof console.debug;
  } | null = null;
  private originalOnError: OnErrorEventHandler | null = null;
  private originalOnUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(options: ConsoleOptions, onCapture: (log: ConsoleLog) => void) {
    this.options = {
      levels: options.levels || ['log', 'warn', 'error', 'info'],
      maxLogs: options.maxLogs ?? 500,
      maxPayloadSize: options.maxPayloadSize ?? 5000,
      captureStackTrace: options.captureStackTrace ?? true,
    };
    this.onCapture = onCapture;
  }

  start(): void {
    if (this.isMonitoring || typeof console === 'undefined') return;
    this.isMonitoring = true;
    this.interceptConsole();
    this.interceptGlobalErrors();
  }

  stop(): void {
    if (!this.isMonitoring) return;
    this.isMonitoring = false;
    this.restoreConsole();
    this.restoreGlobalErrors();
  }

  getLogs(): ConsoleLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  private stringifyArg(arg: unknown, seen = new WeakSet()): string {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';

    const type = typeof arg;
    if (type === 'string') return arg as string;
    if (type === 'number' || type === 'boolean') return String(arg);
    if (type === 'function') return `[Function: ${(arg as () => void).name || 'anonymous'}]`;
    if (type === 'symbol') return arg.toString();

    if (type === 'object') {
      // Handle circular references
      if (seen.has(arg as object)) return '[Circular]';
      seen.add(arg as object);

      if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}`;
      }

      try {
        const str = JSON.stringify(arg, (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) return '[Circular]';
            seen.add(value);
          }
          if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
          if (typeof value === 'symbol') return value.toString();
          return value;
        });
        return str.length > this.options.maxPayloadSize ? str.slice(0, this.options.maxPayloadSize) + '...' : str;
      } catch {
        return '[Object]';
      }
    }

    return String(arg);
  }

  private getStackTrace(): string[] {
    if (!this.options.captureStackTrace) return [];

    try {
      const stack = new Error().stack || '';
      const lines = stack.split('\n').slice(3); // Skip Error, this function, and caller
      return lines.slice(0, 5).map((line) => line.trim());
    } catch {
      return [];
    }
  }

  private addLog(level: ConsoleLog['level'], args: unknown[]): void {
    if (this.inStack) return;
    this.inStack = true;

    try {
      const payload = args.map((arg) => this.stringifyArg(arg));
      const log: ConsoleLog = {
        level,
        payload,
        trace: this.getStackTrace(),
        timestamp: Date.now(),
      };

      this.logs.push(log);
      if (this.logs.length > this.options.maxLogs) this.logs.shift();
      this.onCapture(log);
    } finally {
      this.inStack = false;
    }
  }

  private interceptConsole(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };

    const levels = this.options.levels;

    if (levels.includes('log')) {
      console.log = function (...args: unknown[]) {
        self.addLog('log', args);
        return self.originalConsole!.log(...args);
      };
    }

    if (levels.includes('warn')) {
      console.warn = function (...args: unknown[]) {
        self.addLog('warn', args);
        return self.originalConsole!.warn(...args);
      };
    }

    if (levels.includes('error')) {
      console.error = function (...args: unknown[]) {
        self.addLog('error', args);
        return self.originalConsole!.error(...args);
      };
    }

    if (levels.includes('info')) {
      console.info = function (...args: unknown[]) {
        self.addLog('info', args);
        return self.originalConsole!.info(...args);
      };
    }

    if (levels.includes('debug')) {
      console.debug = function (...args: unknown[]) {
        self.addLog('debug', args);
        return self.originalConsole!.debug(...args);
      };
    }
  }

  private restoreConsole(): void {
    if (this.originalConsole) {
      console.log = this.originalConsole.log;
      console.warn = this.originalConsole.warn;
      console.error = this.originalConsole.error;
      console.info = this.originalConsole.info;
      console.debug = this.originalConsole.debug;
      this.originalConsole = null;
    }
  }

  private interceptGlobalErrors(): void {
    if (typeof window === 'undefined') return;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    this.originalOnError = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
      self.addLog('error', [
        `Uncaught error: ${message}`,
        `at ${source}:${lineno}:${colno}`,
        error?.stack || '',
      ]);
      if (self.originalOnError && typeof self.originalOnError === 'function') {
        return self.originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };

    this.originalOnUnhandledRejection = window.onunhandledrejection as
      | ((event: PromiseRejectionEvent) => void)
      | null;
    window.onunhandledrejection = function (event: PromiseRejectionEvent) {
      self.addLog('error', [`Unhandled promise rejection: ${self.stringifyArg(event.reason)}`]);
      if (self.originalOnUnhandledRejection) {
        self.originalOnUnhandledRejection(event);
      }
    };
  }

  private restoreGlobalErrors(): void {
    if (typeof window === 'undefined') return;

    if (this.originalOnError !== null) {
      window.onerror = this.originalOnError;
      this.originalOnError = null;
    }

    if (this.originalOnUnhandledRejection !== null) {
      window.onunhandledrejection = this.originalOnUnhandledRejection;
      this.originalOnUnhandledRejection = null;
    }
  }
}

// ============================================================================
// People API
// ============================================================================

class PeopleAPI {
  private sdk: BentoLabsSDK;

  constructor(sdk: BentoLabsSDK) {
    this.sdk = sdk;
  }

  /**
   * Set properties on the current user
   */
  set(properties: UserProperties): void {
    this.sdk.capture('$set', { $set: properties });
  }

  /**
   * Set properties on the current user only if they haven't been set already
   */
  setOnce(properties: UserProperties): void {
    this.sdk.capture('$set', { $set_once: properties });
  }

  /**
   * Remove properties from the current user
   */
  unset(propertyNames: string[]): void {
    this.sdk.capture('$set', { $unset: propertyNames });
  }
}

// ============================================================================
// Main SDK Class
// ============================================================================

export class BentoLabsSDK {
  private config: SDKConfig;
  private sessionId: string;
  private events: (BentoEvent | RetryableBentoEvent)[];
  private isRecording: boolean;
  private batchTimer: ReturnType<typeof setInterval> | null;
  private retryTimer: ReturnType<typeof setTimeout> | null;
  private isSending: boolean;
  private stopRecordingFn: (() => void) | null;

  // PostHog-like state
  private distinctId: string;
  private identity: UserIdentity;
  private superProperties: Record<string, unknown>;
  private persistence: Persistence | null;
  private initialized: boolean;
  private optedOut: boolean;

  // Monitoring
  private networkMonitor: NetworkMonitor | null;
  private storageMonitor: StorageMonitor | null;
  private consoleMonitor: ConsoleMonitor | null;

  // People API
  public people: PeopleAPI;

  // Autocapture cleanup functions
  private autocaptureCleanup: (() => void) | null;

  constructor() {
    this.config = {
      apiKey: '',
      endpoint: '',
      debug: false,
      batchSize: 50,
      batchInterval: 10000,
      enableRecording: true,
      maxRetries: 3,
      baseRetryDelay: 1000,
      enableCompression: true,
      // PostHog-like defaults
      persistence: 'localStorage+cookie',
      respect_dnt: false,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      // Monitoring defaults
      capture_network: true,
      networkOptions: {
        skipUrls: ['api.bentolabs.ai'],
        captureHeaders: false,
        captureBody: false,
        maxBodySize: 5000,
        sampleRate: 1,
      },
      capture_storage: true,
      storageOptions: {
        localStorage: true,
        sessionStorage: true,
        cookies: true,
        sensitiveKeys: ['password', 'token', 'secret', 'api_key', 'apikey', 'auth', 'credential'],
      },
      capture_console: true,
      consoleOptions: {
        levels: ['log', 'warn', 'error', 'info'],
        maxLogs: 500,
        maxPayloadSize: 5000,
        captureStackTrace: true,
      },
      // Web Vitals defaults
      capture_web_vitals: true,
      webVitalsOptions: {
        allowedMetrics: ['LCP', 'FCP', 'INP', 'CLS'],
        captureAttribution: true,
      },
    };

    this.sessionId = '';
    this.events = [];
    this.isRecording = false;
    this.batchTimer = null;
    this.retryTimer = null;
    this.isSending = false;
    this.stopRecordingFn = null;

    // PostHog-like state
    this.distinctId = '';
    this.identity = {
      distinctId: null,
      properties: {},
      isIdentified: false,
    };
    this.superProperties = {};
    this.persistence = null;
    this.initialized = false;
    this.optedOut = false;

    // Monitoring
    this.networkMonitor = null;
    this.storageMonitor = null;
    this.consoleMonitor = null;

    // People API
    this.people = new PeopleAPI(this);

    // Autocapture
    this.autocaptureCleanup = null;
  }

  /**
   * Initialize the BentoLabs SDK
   */
  public init(apiKey: string, options?: SDKOptions): void {
    // Stop any existing recording before reinitializing
    if (this.isRecording || this.stopRecordingFn) {
      this.stopRecordingInternal();
    }

    // Merge options with defaults
    const defaultOptions = {
      endpoint: 'https://api.bentolabs.ai',
      debug: false,
      batchSize: 100,
      batchInterval: 5000, // 5 seconds (PostHog-style)
      enableRecording: true,
      maxRetries: 3,
      baseRetryDelay: 1000,
      enableCompression: true,
      // PostHog-like defaults
      persistence: 'localStorage+cookie' as const,
      respect_dnt: false,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      // Monitoring defaults
      capture_network: true,
      networkOptions: {
        skipUrls: ['api.bentolabs.ai'],
        captureHeaders: false,
        captureBody: false,
        maxBodySize: 5000,
        sampleRate: 1,
      },
      capture_storage: true,
      storageOptions: {
        localStorage: true,
        sessionStorage: true,
        cookies: true,
        sensitiveKeys: ['password', 'token', 'secret', 'api_key', 'apikey', 'auth', 'credential'],
      },
      capture_console: true,
      consoleOptions: {
        levels: ['log', 'warn', 'error', 'info'] as ('log' | 'warn' | 'error' | 'info' | 'debug')[],
        maxLogs: 500,
        maxPayloadSize: 5000,
        captureStackTrace: true,
      },
      // Web Vitals defaults
      capture_web_vitals: true,
      webVitalsOptions: {
        allowedMetrics: ['LCP', 'FCP', 'INP', 'CLS'] as ('LCP' | 'FCP' | 'INP' | 'CLS' | 'TTFB')[],
        captureAttribution: true,
      },
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      networkOptions: { ...defaultOptions.networkOptions, ...options?.networkOptions },
      storageOptions: { ...defaultOptions.storageOptions, ...options?.storageOptions },
      consoleOptions: { ...defaultOptions.consoleOptions, ...options?.consoleOptions },
      webVitalsOptions: { ...defaultOptions.webVitalsOptions, ...options?.webVitalsOptions },
    };

    // Set up configuration
    this.config = {
      apiKey,
      endpoint: mergedOptions.endpoint,
      debug: mergedOptions.debug,
      batchSize: mergedOptions.batchSize,
      batchInterval: mergedOptions.batchInterval,
      enableRecording: mergedOptions.enableRecording,
      maxRetries: mergedOptions.maxRetries,
      baseRetryDelay: mergedOptions.baseRetryDelay,
      enableCompression: mergedOptions.enableCompression ?? true,
      persistence: mergedOptions.persistence,
      respect_dnt: mergedOptions.respect_dnt,
      autocapture: mergedOptions.autocapture,
      capture_pageview: mergedOptions.capture_pageview,
      capture_pageleave: mergedOptions.capture_pageleave,
      capture_network: mergedOptions.capture_network,
      networkOptions: mergedOptions.networkOptions as Required<NetworkOptions>,
      capture_storage: mergedOptions.capture_storage,
      storageOptions: mergedOptions.storageOptions as Required<StorageOptions>,
      capture_console: mergedOptions.capture_console,
      consoleOptions: mergedOptions.consoleOptions as Required<ConsoleOptions>,
      capture_web_vitals: mergedOptions.capture_web_vitals,
      webVitalsOptions: mergedOptions.webVitalsOptions as Required<WebVitalsOptions>,
      bootstrap: mergedOptions.bootstrap,
    };

    // Check Do Not Track
    if (this.config.respect_dnt && this.isDNTEnabled()) {
      if (this.config.debug) {
        console.log('[BentoLabsSDK] Do Not Track is enabled, opting out');
      }
      this.optedOut = true;
    }

    // Initialize persistence
    this.persistence = new Persistence(this.config.persistence);

    // Load persisted state
    this.loadPersistedState();

    // Handle bootstrap
    if (this.config.bootstrap?.distinctID) {
      this.distinctId = this.config.bootstrap.distinctID;
      this.identity.distinctId = this.config.bootstrap.distinctID;
      this.identity.isIdentified = this.config.bootstrap.isIdentifiedID ?? false;
      this.persistence.set(PERSISTENCE_KEYS.DISTINCT_ID, this.distinctId);
      if (this.identity.isIdentified) {
        this.persistence.set(PERSISTENCE_KEYS.IS_IDENTIFIED, 'true');
      }
    }

    // Ensure we have a distinct ID
    if (!this.distinctId) {
      this.distinctId = this.generateAnonymousId();
      this.persistence.set(PERSISTENCE_KEYS.DISTINCT_ID, this.distinctId);
    }

    // Generate session ID
    this.sessionId = this.generateSessionId();
    this.initialized = true;

    // Log initialization
    if (this.config.debug) {
      console.log('[BentoLabsSDK] Initialized with config:', {
        apiKey: apiKey.substring(0, 8) + '...',
        endpoint: this.config.endpoint,
        debug: this.config.debug,
        sessionId: this.sessionId,
        distinctId: this.distinctId,
      });
    }

    // Start recording
    this.startRecording();
    this.startBatching();

    // Setup autocapture
    if (this.config.autocapture) {
      this.setupAutocapture();
    }

    // Capture pageview
    if (this.config.capture_pageview) {
      this.capture('$pageview', {
        $current_url: typeof window !== 'undefined' ? window.location.href : '',
        $pathname: typeof window !== 'undefined' ? window.location.pathname : '',
        $referrer: typeof document !== 'undefined' ? document.referrer : '',
      });
    }

    // Setup page leave tracking
    if (this.config.capture_pageleave && typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.handlePageLeave.bind(this));
    }

    // Initialize monitors
    if (this.config.capture_network) {
      this.startNetworkCapture();
    }
    if (this.config.capture_storage) {
      this.startStorageCapture();
    }
    if (this.config.capture_console) {
      this.startConsoleCapture();
    }
    if (this.config.capture_web_vitals) {
      this.startWebVitalsCapture();
    }
  }

  // ============================================================================
  // Identity Methods
  // ============================================================================

  /**
   * Identify a user with a distinct ID and optional properties
   */
  public identify(distinctId: string, properties?: UserProperties): void {
    if (!this.initialized) {
      console.warn('[BentoLabsSDK] Cannot identify: SDK not initialized');
      return;
    }

    const previousId = this.distinctId;
    this.distinctId = distinctId;
    this.identity.distinctId = distinctId;
    this.identity.isIdentified = true;
    if (properties) {
      this.identity.properties = { ...this.identity.properties, ...properties };
    }

    // Persist
    this.persistence?.set(PERSISTENCE_KEYS.DISTINCT_ID, distinctId);
    this.persistence?.set(PERSISTENCE_KEYS.IS_IDENTIFIED, 'true');
    if (properties) {
      this.persistence?.set(PERSISTENCE_KEYS.USER_PROPERTIES, JSON.stringify(this.identity.properties));
    }

    // Capture identify event
    this.capture('$identify', {
      $anon_distinct_id: previousId,
      ...properties,
    });

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Identified user:', distinctId);
    }
  }

  /**
   * Reset the user to an anonymous state
   */
  public reset(): void {
    if (!this.initialized) return;

    // Generate new anonymous ID
    this.distinctId = this.generateAnonymousId();
    this.identity = {
      distinctId: null,
      properties: {},
      isIdentified: false,
    };
    this.superProperties = {};

    // Clear persisted state
    this.persistence?.clear();

    // Persist new anonymous ID
    this.persistence?.set(PERSISTENCE_KEYS.DISTINCT_ID, this.distinctId);

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Reset to anonymous:', this.distinctId);
    }
  }

  /**
   * Get the current distinct ID
   */
  public getDistinctId(): string {
    return this.distinctId;
  }

  // ============================================================================
  // Super Properties Methods
  // ============================================================================

  /**
   * Register super properties to be sent with every event
   */
  public register(properties: Record<string, unknown>): void {
    this.superProperties = { ...this.superProperties, ...properties };
    this.persistence?.set(PERSISTENCE_KEYS.SUPER_PROPERTIES, JSON.stringify(this.superProperties));

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Registered super properties:', properties);
    }
  }

  /**
   * Register super properties only if they haven't been set
   */
  public registerOnce(properties: Record<string, unknown>): void {
    const newProps: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (!(key in this.superProperties)) {
        newProps[key] = value;
      }
    }
    if (Object.keys(newProps).length > 0) {
      this.register(newProps);
    }
  }

  /**
   * Remove a super property
   */
  public unregister(propertyName: string): void {
    delete this.superProperties[propertyName];
    this.persistence?.set(PERSISTENCE_KEYS.SUPER_PROPERTIES, JSON.stringify(this.superProperties));

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Unregistered super property:', propertyName);
    }
  }

  // ============================================================================
  // Event Capture
  // ============================================================================

  /**
   * Capture an event
   */
  public capture(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      console.warn('[BentoLabsSDK] Cannot capture event: SDK not initialized');
      return;
    }

    if (this.optedOut) {
      if (this.config.debug) {
        console.log('[BentoLabsSDK] Event not captured (opted out):', eventName);
      }
      return;
    }

    const event: CapturedEvent = {
      event: eventName,
      properties: {
        ...this.superProperties,
        ...properties,
        $current_url: typeof window !== 'undefined' ? window.location.href : '',
        $pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      },
      $timestamp: Date.now(),
      $session_id: this.sessionId,
      $distinct_id: this.distinctId,
    };

    this.addEvent({
      timestamp: event.$timestamp,
      type: `capture:${eventName}`,
      data: event,
      sessionId: this.sessionId,
    });

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Captured event:', eventName, properties);
    }
  }

  /**
   * Track a custom event (legacy method, delegates to capture)
   */
  public trackCustomEvent(eventName: string, data?: Record<string, unknown>): void {
    if (!this.sessionId) {
      console.warn('[BentoLabsSDK] Cannot track event: SDK not initialized');
      return;
    }

    this.capture(`custom:${eventName}`, data);
  }

  // ============================================================================
  // Opt-in/Opt-out
  // ============================================================================

  /**
   * Opt out of tracking
   */
  public opt_out_capturing(): void {
    this.optedOut = true;
    this.persistence?.set(PERSISTENCE_KEYS.OPTED_OUT, 'true');

    // Stop recording
    if (this.isRecording) {
      this.stopRecordingInternal();
    }

    // Stop monitors
    this.stopNetworkCapture();
    this.stopStorageCapture();
    this.stopConsoleCapture();

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Opted out of capturing');
    }
  }

  /**
   * Opt back into tracking
   */
  public opt_in_capturing(): void {
    this.optedOut = false;
    this.persistence?.remove(PERSISTENCE_KEYS.OPTED_OUT);

    // Resume recording
    if (this.initialized && this.config.enableRecording) {
      this.startRecording();
    }

    // Resume monitors
    if (this.config.capture_network) {
      this.startNetworkCapture();
    }
    if (this.config.capture_storage) {
      this.startStorageCapture();
    }
    if (this.config.capture_console) {
      this.startConsoleCapture();
    }

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Opted back into capturing');
    }
  }

  /**
   * Check if user has opted out
   */
  public has_opted_out_capturing(): boolean {
    return this.optedOut;
  }

  /**
   * Check if user has opted in (not opted out)
   */
  public has_opted_in_capturing(): boolean {
    return !this.optedOut;
  }

  // ============================================================================
  // Monitoring Control
  // ============================================================================

  /**
   * Start network request monitoring
   */
  public startNetworkCapture(): void {
    if (this.networkMonitor) return;

    this.networkMonitor = new NetworkMonitor(this.config.networkOptions, (log) => {
      if (!this.optedOut) {
        this.capture('$network_request', log as unknown as Record<string, unknown>);
      }
    });
    this.networkMonitor.start();

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Network capture started');
    }
  }

  /**
   * Stop network request monitoring
   */
  public stopNetworkCapture(): void {
    if (this.networkMonitor) {
      this.networkMonitor.stop();
      this.networkMonitor = null;

      if (this.config.debug) {
        console.log('[BentoLabsSDK] Network capture stopped');
      }
    }
  }

  /**
   * Start storage monitoring
   */
  public startStorageCapture(): void {
    if (this.storageMonitor) return;

    this.storageMonitor = new StorageMonitor(this.config.storageOptions, (log) => {
      if (!this.optedOut) {
        this.capture('$storage_event', log as unknown as Record<string, unknown>);
      }
    });
    this.storageMonitor.start();

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Storage capture started');
    }
  }

  /**
   * Stop storage monitoring
   */
  public stopStorageCapture(): void {
    if (this.storageMonitor) {
      this.storageMonitor.stop();
      this.storageMonitor = null;

      if (this.config.debug) {
        console.log('[BentoLabsSDK] Storage capture stopped');
      }
    }
  }

  /**
   * Start console monitoring
   */
  public startConsoleCapture(): void {
    if (this.consoleMonitor) return;

    this.consoleMonitor = new ConsoleMonitor(this.config.consoleOptions, (log) => {
      if (!this.optedOut) {
        this.capture('$console_log', log as unknown as Record<string, unknown>);
      }
    });
    this.consoleMonitor.start();

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Console capture started');
    }
  }

  /**
   * Stop console monitoring
   */
  public stopConsoleCapture(): void {
    if (this.consoleMonitor) {
      this.consoleMonitor.stop();
      this.consoleMonitor = null;

      if (this.config.debug) {
        console.log('[BentoLabsSDK] Console capture stopped');
      }
    }
  }

  /**
   * Get network logs
   */
  public getNetworkLogs(): NetworkLog[] {
    return this.networkMonitor?.getLogs() || [];
  }

  /**
   * Get storage logs
   */
  public getStorageLogs(): StorageLog[] {
    return this.storageMonitor?.getLogs() || [];
  }

  /**
   * Get console logs
   */
  public getConsoleLogs(): ConsoleLog[] {
    return this.consoleMonitor?.getLogs() || [];
  }

  /**
   * Start Web Vitals monitoring
   * Captures LCP, FCP, INP, CLS, and optionally TTFB
   */
  public startWebVitalsCapture(): void {
    if (typeof window === 'undefined') return;

    const allowedMetrics = this.config.webVitalsOptions.allowedMetrics || ['LCP', 'FCP', 'INP', 'CLS'];
    const captureAttribution = this.config.webVitalsOptions.captureAttribution ?? true;

    const captureMetric = (metric: Metric, metricName: string) => {
      if (this.optedOut) return;

      const properties: Record<string, unknown> = {
        [`$web_vitals_${metricName}_value`]: metric.value,
        $pathname: window.location.pathname,
        $current_url: window.location.href,
      };

      // Add full event data if attribution capture is enabled
      if (captureAttribution) {
        properties[`$web_vitals_${metricName}_event`] = {
          name: metric.name,
          value: metric.value,
          rating: metric.rating,
          delta: metric.delta,
          id: metric.id,
          navigationType: metric.navigationType,
          attribution: 'attribution' in metric ? metric.attribution : undefined,
        };
      }

      this.capture('$web_vitals', properties);

      if (this.config.debug) {
        console.log(`[BentoLabsSDK] Web Vital captured: ${metricName} = ${metric.value} (${metric.rating})`);
      }
    };

    // Subscribe to each allowed metric
    if (allowedMetrics.includes('LCP')) {
      onLCP((metric: LCPMetric) => captureMetric(metric, 'LCP'));
    }
    if (allowedMetrics.includes('FCP')) {
      onFCP((metric: FCPMetric) => captureMetric(metric, 'FCP'));
    }
    if (allowedMetrics.includes('INP')) {
      onINP((metric: INPMetric) => captureMetric(metric, 'INP'));
    }
    if (allowedMetrics.includes('CLS')) {
      onCLS((metric: CLSMetric) => captureMetric(metric, 'CLS'));
    }
    if (allowedMetrics.includes('TTFB')) {
      onTTFB((metric: TTFBMetric) => captureMetric(metric, 'TTFB'));
    }

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Web Vitals capture started for:', allowedMetrics);
    }
  }

  // ============================================================================
  // Existing Public Methods (Preserved)
  // ============================================================================

  /**
   * Stop recording and clean up resources
   */
  public stop(): void {
    if (this.config.debug) {
      console.log('[BentoLabsSDK] Stopping recording and batching');
    }

    this.stopRecordingInternal();

    // Stop monitors
    this.stopNetworkCapture();
    this.stopStorageCapture();
    this.stopConsoleCapture();

    // Send any remaining events
    if (this.events.length > 0) {
      this.sendBatch();
    }

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Stopped successfully');
    }
  }

  /**
   * Get current session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Check if recording is active
   */
  public isRecordingActive(): boolean {
    return this.isRecording;
  }

  /**
   * Get current configuration (without exposing sensitive data)
   */
  public getConfig(): Omit<SDKConfig, 'apiKey'> & { apiKey: string } {
    return {
      apiKey: this.config.apiKey ? this.config.apiKey.substring(0, 8) + '...' : '',
      endpoint: this.config.endpoint,
      debug: this.config.debug,
      batchSize: this.config.batchSize,
      batchInterval: this.config.batchInterval,
      enableRecording: this.config.enableRecording,
      maxRetries: this.config.maxRetries,
      baseRetryDelay: this.config.baseRetryDelay,
      enableCompression: this.config.enableCompression,
      persistence: this.config.persistence,
      respect_dnt: this.config.respect_dnt,
      autocapture: this.config.autocapture,
      capture_pageview: this.config.capture_pageview,
      capture_pageleave: this.config.capture_pageleave,
      capture_network: this.config.capture_network,
      networkOptions: this.config.networkOptions,
      capture_storage: this.config.capture_storage,
      storageOptions: this.config.storageOptions,
      capture_console: this.config.capture_console,
      consoleOptions: this.config.consoleOptions,
      capture_web_vitals: this.config.capture_web_vitals,
      webVitalsOptions: this.config.webVitalsOptions,
    };
  }

  /**
   * Get current event queue length
   */
  public getEventQueueLength(): number {
    return this.events.length;
  }

  /**
   * Manually trigger a batch send
   */
  public async flushEvents(): Promise<void> {
    await this.sendBatch();
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private generateSessionId(): string {
    const uuid = this.generateUUID();
    return `sess_${uuid}`;
  }

  private generateAnonymousId(): string {
    const uuid = this.generateUUID();
    return `anon_${uuid}`;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private isDNTEnabled(): boolean {
    if (typeof navigator === 'undefined') return false;
    return navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes';
  }

  private loadPersistedState(): void {
    if (!this.persistence) return;

    // Load distinct ID
    const storedDistinctId = this.persistence.get(PERSISTENCE_KEYS.DISTINCT_ID);
    if (storedDistinctId) {
      this.distinctId = storedDistinctId;
      this.identity.distinctId = storedDistinctId;
    }

    // Load identified status
    const isIdentified = this.persistence.get(PERSISTENCE_KEYS.IS_IDENTIFIED);
    if (isIdentified === 'true') {
      this.identity.isIdentified = true;
    }

    // Load super properties
    const storedSuperProps = this.persistence.get(PERSISTENCE_KEYS.SUPER_PROPERTIES);
    if (storedSuperProps) {
      try {
        this.superProperties = JSON.parse(storedSuperProps);
      } catch {
        this.superProperties = {};
      }
    }

    // Load user properties
    const storedUserProps = this.persistence.get(PERSISTENCE_KEYS.USER_PROPERTIES);
    if (storedUserProps) {
      try {
        this.identity.properties = JSON.parse(storedUserProps);
      } catch {
        this.identity.properties = {};
      }
    }

    // Load opted out status
    const optedOut = this.persistence.get(PERSISTENCE_KEYS.OPTED_OUT);
    if (optedOut === 'true') {
      this.optedOut = true;
    }
  }

  private startRecording(): void {
    if (!this.config.enableRecording) {
      if (this.config.debug) {
        console.log('[BentoLabsSDK] Recording disabled in configuration');
      }
      return;
    }

    if (this.optedOut) {
      if (this.config.debug) {
        console.log('[BentoLabsSDK] Recording disabled (opted out)');
      }
      return;
    }

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Starting recording for session:', this.sessionId);
    }

    try {
      if (this.config.debug) {
        console.log('[BentoLabsSDK] Calling rrweb record()...');
      }

      const stopFn = record({
        emit: (event: eventWithTime) => {
          if (this.config.debug) {
            console.log('[BentoLabsSDK] Event captured:', event.type);
          }
          this.addEvent({
            timestamp: event.timestamp,
            type: event.type.toString(),
            data: event,
            sessionId: this.sessionId,
          });
        },
        recordCanvas: true,
        collectFonts: true,
        plugins: [],
        captureElementContext: true,
        elementContextOptions: {
          maxTextLength: 500,
          includeOuterHTML: true,
          outerHTMLDepth: 2,
          includeInputValues: true,
        },
        maskInputOptions: {
          password: true,
        },
        sampling: {
          mousemove: true,
          mouseInteraction: { click: false, dblclick: false },
          scroll: 150,
          input: 'last',
        },
        checkoutEveryNms: 5 * 60 * 1000,
      });

      if (typeof stopFn === 'function') {
        this.stopRecordingFn = stopFn;
        this.isRecording = true;
        if (this.config.debug) {
          console.log('[BentoLabsSDK] Recording started successfully');
        }
      } else {
        console.error(
          '[BentoLabsSDK] rrweb record() did not return a stop function. Recording may not have started properly.'
        );
        this.stopRecordingFn = null;
        this.isRecording = false;
      }
    } catch (error) {
      console.error('[BentoLabsSDK] Failed to start recording:', error);
      this.isRecording = false;
    }
  }

  private startBatching(): void {
    if (this.config.debug) {
      console.log('[BentoLabsSDK] Starting event batching');
    }

    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    this.batchTimer = setInterval(() => {
      this.sendBatch();
    }, this.config.batchInterval);

    if (this.config.debug) {
      console.log(`[BentoLabsSDK] Batch timer set for ${this.config.batchInterval}ms intervals`);
    }
  }

  private addEvent(event: BentoEvent): void {
    if (this.optedOut) return;

    this.events.push(event);

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Event added:', event.type);
    }

    // Only trigger immediate send if we've accumulated a lot of events AND we're not already sending
    // This prevents spamming when batch size is reached during high event volume
    if (this.events.length >= this.config.batchSize && !this.isSending) {
      if (this.config.debug) {
        console.log(`[BentoLabsSDK] Batch size limit reached (${this.config.batchSize}), sending batch`);
      }
      this.sendBatch();
    }
  }

  private async sendBatch(): Promise<void> {
    // Prevent concurrent sends
    if (this.isSending) {
      if (this.config.debug) {
        console.log('[BentoLabsSDK] Already sending, skipping batch');
      }
      return;
    }

    const now = Date.now();
    const readyEvents = this.events.filter((event) => {
      if ('nextRetryTime' in event) {
        return now >= event.nextRetryTime;
      }
      return true;
    });

    if (readyEvents.length === 0) {
      return;
    }

    this.isSending = true;

    // Remove events we're about to send from the queue
    this.events = this.events.filter((event) => !readyEvents.includes(event));

    const payload: BatchPayload = {
      sessionId: this.sessionId,
      events: readyEvents,
      timestamp: now,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      distinctId: this.distinctId,
    };

    if (this.config.debug) {
      console.log(`[BentoLabsSDK] Sending batch with ${readyEvents.length} events`);
    }

    try {
      const jsonPayload = JSON.stringify(payload);
      let body: BodyInit = jsonPayload;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
        'X-Session-ID': this.sessionId,
      };

      // Compress if enabled and CompressionStream is available
      if (this.config.enableCompression && typeof CompressionStream !== 'undefined') {
        try {
          const encoder = new TextEncoder();
          const data = encoder.encode(jsonPayload);
          const cs = new CompressionStream('gzip');
          const writer = cs.writable.getWriter();
          writer.write(data);
          writer.close();

          const compressedChunks: Uint8Array[] = [];
          const reader = cs.readable.getReader();
          let result = await reader.read();
          while (!result.done) {
            compressedChunks.push(result.value);
            result = await reader.read();
          }

          // Combine chunks into single Uint8Array
          const totalLength = compressedChunks.reduce((acc, chunk) => acc + chunk.length, 0);
          const compressedData = new Uint8Array(totalLength);
          let offset = 0;
          for (const chunk of compressedChunks) {
            compressedData.set(chunk, offset);
            offset += chunk.length;
          }

          body = new Blob([compressedData], { type: 'application/json' });
          headers['Content-Encoding'] = 'gzip';

          if (this.config.debug) {
            const ratio = ((1 - compressedData.length / data.length) * 100).toFixed(1);
            console.log(
              `[BentoLabsSDK] Compressed payload: ${data.length} -> ${compressedData.length} bytes (${ratio}% reduction)`
            );
          }
        } catch (compressionError) {
          if (this.config.debug) {
            console.warn('[BentoLabsSDK] Compression failed, sending uncompressed:', compressionError);
          }
          // Fall back to uncompressed
        }
      }

      const response = await fetch(`${this.config.endpoint}/events/`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (this.config.debug) {
        console.log('[BentoLabsSDK] Batch sent successfully');
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('[BentoLabsSDK] Failed to send batch:', error);
      }

      // Only retry if we haven't exceeded max retries
      const retryableEvents: RetryableBentoEvent[] = readyEvents.map((event) => {
        const retryCount = 'retryCount' in event ? event.retryCount + 1 : 1;
        // Exponential backoff: 1s, 2s, 4s, 8s, etc.
        const delay = this.config.baseRetryDelay * Math.pow(2, retryCount - 1);

        return {
          ...event,
          retryCount,
          nextRetryTime: now + delay,
        };
      });

      const eventsToRetry = retryableEvents.filter((event) => event.retryCount <= this.config.maxRetries);
      const droppedEvents = retryableEvents.length - eventsToRetry.length;

      if (droppedEvents > 0 && this.config.debug) {
        console.log(`[BentoLabsSDK] Dropped ${droppedEvents} events after max retries`);
      }

      // Re-add events to retry (they'll be picked up on next batch interval)
      if (eventsToRetry.length > 0) {
        this.events.unshift(...eventsToRetry);

        if (this.config.debug) {
          const nextRetryIn = Math.min(...eventsToRetry.map((e) => e.nextRetryTime)) - now;
          console.log(`[BentoLabsSDK] ${eventsToRetry.length} events re-queued for retry in ${nextRetryIn}ms`);
        }

        // Schedule a single retry instead of calling scheduleRetry which can cause loops
        // The batch interval will handle retries naturally
      }
    } finally {
      this.isSending = false;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    const now = Date.now();
    const retryableEvents = this.events.filter((event) => 'nextRetryTime' in event) as RetryableBentoEvent[];

    if (retryableEvents.length === 0) {
      return;
    }

    const nextRetryTime = Math.min(...retryableEvents.map((e) => e.nextRetryTime));
    const delay = Math.max(0, nextRetryTime - now);

    if (this.config.debug) {
      console.log(`[BentoLabsSDK] Scheduling retry in ${delay}ms`);
    }

    this.retryTimer = setTimeout(() => {
      this.sendBatch();
    }, delay);
  }

  private stopRecordingInternal(): void {
    if (this.stopRecordingFn) {
      this.stopRecordingFn();
      this.stopRecordingFn = null;
    }

    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    if (this.autocaptureCleanup) {
      this.autocaptureCleanup();
      this.autocaptureCleanup = null;
    }

    this.isRecording = false;
  }

  private setupAutocapture(): void {
    if (typeof document === 'undefined') return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Check if element is in allowlist
      const tagName = target.tagName.toLowerCase();
      const allowedTags = ['a', 'button', 'input', 'select', 'textarea'];
      const isAllowed =
        allowedTags.includes(tagName) ||
        target.getAttribute('role') === 'button' ||
        target.hasAttribute('data-bento-capture');

      if (!isAllowed) return;

      let selector = '';
      try {
        selector = generateSelectorFromElement(target);
      } catch {
        selector = '';
      }

      this.capture('$autocapture', {
        $event_type: 'click',
        $element_tag: tagName,
        $element_text: target.textContent?.slice(0, 100) || '',
        $element_classes: target.className || '',
        $element_id: target.id || '',
        $bento_selector: selector,
      });
    };

    const handleChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target) return;

      const tagName = target.tagName.toLowerCase();
      if (!['input', 'select', 'textarea'].includes(tagName)) return;

      // Don't capture password fields
      if (target.type === 'password') return;

      let selector = '';
      try {
        selector = generateSelectorFromElement(target);
      } catch {
        selector = '';
      }

      this.capture('$autocapture', {
        $event_type: 'change',
        $element_tag: tagName,
        $element_type: target.type || '',
        $element_name: target.name || '',
        $element_id: target.id || '',
        $bento_selector: selector,
        // Don't capture actual value for privacy
      });
    };

    const handleSubmit = (e: SubmitEvent) => {
      const target = e.target as HTMLFormElement;
      if (!target || target.tagName.toLowerCase() !== 'form') return;

      let selector = '';
      try {
        selector = generateSelectorFromElement(target);
      } catch {
        selector = '';
      }

      this.capture('$autocapture', {
        $event_type: 'submit',
        $element_tag: 'form',
        $form_name: target.name || '',
        $form_id: target.id || '',
        $form_action: target.action || '',
        $bento_selector: selector,
      });
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('submit', handleSubmit, true);

    this.autocaptureCleanup = () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('change', handleChange, true);
      document.removeEventListener('submit', handleSubmit, true);
    };

    if (this.config.debug) {
      console.log('[BentoLabsSDK] Autocapture setup complete');
    }
  }

  private handlePageLeave(): void {
    this.capture('$pageleave', {
      $current_url: typeof window !== 'undefined' ? window.location.href : '',
      $pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    // Attempt to send any pending events synchronously
    if (this.events.length > 0 && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const payload: BatchPayload = {
        sessionId: this.sessionId,
        events: this.events,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        distinctId: this.distinctId,
      };

      navigator.sendBeacon(
        `${this.config.endpoint}/events/`,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
      this.events = [];
    }
  }
}

// ============================================================================
// Default Instance and Window Export
// ============================================================================

const sdk = new BentoLabsSDK();

if (typeof window !== 'undefined') {
  (window as unknown as { BentoLabsSDK: Record<string, unknown> }).BentoLabsSDK = {
    // Core methods
    init: sdk.init.bind(sdk),
    stop: sdk.stop.bind(sdk),
    getSessionId: sdk.getSessionId.bind(sdk),
    isRecordingActive: sdk.isRecordingActive.bind(sdk),
    getConfig: sdk.getConfig.bind(sdk),
    getEventQueueLength: sdk.getEventQueueLength.bind(sdk),
    flushEvents: sdk.flushEvents.bind(sdk),
    trackCustomEvent: sdk.trackCustomEvent.bind(sdk),

    // Selector generation
    generateSelector,
    generateSelectorFromElement,
    generateSelectorFromHTML,

    // PostHog-like methods
    capture: sdk.capture.bind(sdk),
    identify: sdk.identify.bind(sdk),
    reset: sdk.reset.bind(sdk),
    getDistinctId: sdk.getDistinctId.bind(sdk),
    register: sdk.register.bind(sdk),
    registerOnce: sdk.registerOnce.bind(sdk),
    unregister: sdk.unregister.bind(sdk),
    opt_out_capturing: sdk.opt_out_capturing.bind(sdk),
    opt_in_capturing: sdk.opt_in_capturing.bind(sdk),
    has_opted_out_capturing: sdk.has_opted_out_capturing.bind(sdk),
    has_opted_in_capturing: sdk.has_opted_in_capturing.bind(sdk),
    people: sdk.people,

    // Monitoring control
    startNetworkCapture: sdk.startNetworkCapture.bind(sdk),
    stopNetworkCapture: sdk.stopNetworkCapture.bind(sdk),
    startStorageCapture: sdk.startStorageCapture.bind(sdk),
    stopStorageCapture: sdk.stopStorageCapture.bind(sdk),
    startConsoleCapture: sdk.startConsoleCapture.bind(sdk),
    stopConsoleCapture: sdk.stopConsoleCapture.bind(sdk),
    getNetworkLogs: sdk.getNetworkLogs.bind(sdk),
    getStorageLogs: sdk.getStorageLogs.bind(sdk),
    getConsoleLogs: sdk.getConsoleLogs.bind(sdk),

    // Web Vitals
    startWebVitalsCapture: sdk.startWebVitalsCapture.bind(sdk),
  };
}

export default sdk;
